import { useEffect, useMemo, useState } from 'react';
import { Badge, BlockStack, Box, Button, InlineStack, Link, Modal, Spinner, Text } from '@shopify/polaris';
import { useLookupStore } from '../../../context/useStore.js';
import { getRequest } from '../../../shared/components/functions.jsx';
import { getSystemDocumentFileName } from '../../../shared/components/SharePointPdfPreviewButton.jsx';
import PdfPane from '../../../shared/components/pdf/PdfPane.jsx';
import { getSharePointPath } from '../../../utils/sharepoint.js';

/*
  Purchase history of a product, opened from the pricing card: every purchase order line of the product (newest first)
  with the incoming invoices linked to that order. The PDF of the selected order or invoice is shown on the left; it
  opens on the newest invoice so the last real purchase price is one click away.
*/

const PURCHASE_DOCUMENT_TYPE_ID = 13;

const formatEuro = (value) => `€ ${Number(value || 0).toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatDate = (value) => (value ? String(value).substring(0, 10) : '');

function parseS3Keys(value) {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [parsed];
  } catch {
    return [value];
  }
}

function PurchasePdf({ line, documenttypes }) {
  const [state, setState] = useState({ loading: true, url: null, error: '' });

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    setState({ loading: true, url: null, error: '' });

    (async () => {
      try {
        const fileName = getSystemDocumentFileName('order', line.Bestellingnummer, line.TaalID);
        if (!line.SupplierNumber || !fileName || !documenttypes?.length) throw new Error('No PDF available for this purchase order.');
        const response = await fetch('/api/docgen/streamsharepointdoc', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ path: getSharePointPath(line.SupplierNumber, PURCHASE_DOCUMENT_TYPE_ID, line.Bestellingnummer, documenttypes), fileName })
        });
        if (!response.ok) {
          const data = await response.json().catch(() => null);
          throw new Error(data?.msg || 'The PDF of this purchase order could not be loaded.');
        }
        const blob = await response.blob();
        if (blob.type !== 'application/pdf') throw new Error('The PDF of this purchase order could not be loaded.');
        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) setState({ loading: false, url: objectUrl, error: '' });
      } catch (error) {
        if (!cancelled) setState({ loading: false, url: null, error: error.message });
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [line, documenttypes]);

  if (state.loading) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spinner accessibilityLabel="Loading PDF" size="large" />
      </div>
    );
  }
  if (state.error) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f6f6f7', borderRadius: 8 }}>
        <Text tone="subdued">{state.error}</Text>
      </div>
    );
  }
  return <iframe title={`Purchase order ${line.Bestellingnummer}`} src={state.url} style={{ width: '100%', height: '100%', border: 'none', borderRadius: 8 }} />;
}

export default function PurchaseHistoryModal({ open, onClose, productId }) {
  const documenttypes = useLookupStore((state) => state.documenttypes);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!open || !productId) return;
    let cancelled = false;
    setLoading(true);
    setSelected(null);
    getRequest(`/api/products/${productId}/purchase-history`).then((data) => {
      if (cancelled) return;
      const rows = Array.isArray(data) ? data : [];
      setLines(rows);
      setLoading(false);
      const withInvoice = rows.find((row) => row.LinkedInvoices?.length);
      if (withInvoice) setSelected({ type: 'invoice', line: withInvoice, invoice: withInvoice.LinkedInvoices[0] });
      else if (rows.length) setSelected({ type: 'purchase', line: rows[0] });
    });
    return () => {
      cancelled = true;
    };
  }, [open, productId]);

  const summary = useMemo(() => {
    const priced = lines.filter((line) => Number(line.UnitPurchasePrice) > 0);
    if (!priced.length) return null;
    const prices = priced.map((line) => Number(line.UnitPurchasePrice));
    return {
      last: priced[0],
      min: Math.min(...prices),
      max: Math.max(...prices),
      average: prices.reduce((sum, price) => sum + price, 0) / prices.length,
      orderCount: new Set(lines.map((line) => line.BestellingID)).size
    };
  }, [lines]);

  const isSelected = (type, line, invoice) => selected?.type === type && selected.line.BestellingLijnID === line.BestellingLijnID && (type !== 'invoice' || selected.invoice.id === invoice.id);

  const selectedTitle = !selected
    ? ''
    : selected.type === 'invoice'
    ? `Invoice in ${selected.invoice.number}${selected.invoice.reference ? ` (${selected.invoice.reference})` : ''}`
    : `Purchase order ${selected.line.Bestellingnummer}`;

  return (
    <>
      <Modal open={open} onClose={onClose} title="Purchase history" large>
        <Modal.Section>
          {loading ? (
            <div style={{ height: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Spinner accessibilityLabel="Loading purchase history" size="large" />
            </div>
          ) : !lines.length ? (
            <Text tone="subdued">This product has not been ordered on a purchase order yet.</Text>
          ) : (
            <div style={{ display: 'flex', gap: 16, height: '80vh' }}>
              <div style={{ flex: '3 1 0', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <Box paddingBlockEnd="200">
                  <Text variant="headingSm" as="h3">
                    {selectedTitle}
                  </Text>
                </Box>
                <div style={{ flex: 1, minHeight: 0 }}>
                  {selected?.type === 'invoice' ? (
                    <PdfPane key={selected.invoice.id} keys={parseS3Keys(selected.invoice.s3ObjectKeys)} showFileChooser={false} emptyText="No PDF attached to this invoice." />
                  ) : selected?.type === 'purchase' ? (
                    <PurchasePdf line={selected.line} documenttypes={documenttypes} />
                  ) : null}
                </div>
              </div>

              <div style={{ flex: '2 1 0', minWidth: 0, overflowY: 'auto' }}>
                <BlockStack gap="300">
                  {summary ? (
                    <Box background="bg-surface-secondary" padding="300" borderRadius="200">
                      <BlockStack gap="100">
                        <Text as="p">
                          Last purchase price: <b>{formatEuro(summary.last.UnitPurchasePrice)}</b> ({formatDate(summary.last.Datum)}, {summary.last.SupplierName || 'unknown supplier'})
                        </Text>
                        <Text as="p" tone="subdued">
                          Lowest {formatEuro(summary.min)} · average {formatEuro(summary.average)} · highest {formatEuro(summary.max)} · {summary.orderCount} order
                          {summary.orderCount === 1 ? '' : 's'}
                        </Text>
                      </BlockStack>
                    </Box>
                  ) : null}

                  {lines.map((line) => (
                    <div
                      key={line.BestellingLijnID}
                      style={{
                        border: '1px solid #e3e3e3',
                        borderRadius: 8,
                        padding: 12,
                        background: isSelected('purchase', line) ? '#f1f1f1' : '#fff'
                      }}
                    >
                      <BlockStack gap="150">
                        <InlineStack align="space-between" blockAlign="center" wrap={false} gap="200">
                          <InlineStack gap="200" blockAlign="center">
                            <Link removeUnderline url={'/purchases/' + line.BestellingID} target="_blank">
                              <Text as="span" fontWeight="semibold">
                                {line.Bestellingnummer}
                              </Text>
                            </Link>
                            {line.PurchaseStatusName ? <Badge>{line.PurchaseStatusName}</Badge> : null}
                          </InlineStack>
                          <Text as="span" tone="subdued">
                            {formatDate(line.Datum)}
                          </Text>
                        </InlineStack>
                        <Text as="p">{line.SupplierName || 'Unknown supplier'}</Text>
                        <Text as="p">
                          {Number(line.Qty)} × {Number(line.UnitPurchasePrice) > 0 ? formatEuro(line.UnitPurchasePrice) : 'no price'}
                        </Text>
                        <InlineStack gap="200" blockAlign="center">
                          <Button size="slim" pressed={isSelected('purchase', line)} onClick={() => setSelected({ type: 'purchase', line })}>
                            Order PDF
                          </Button>
                          {line.LinkedInvoices?.map((invoice) => (
                            <Button key={invoice.id} size="slim" pressed={isSelected('invoice', line, invoice)} onClick={() => setSelected({ type: 'invoice', line, invoice })}>
                              {`Invoice ${invoice.number} · ${formatDate(invoice.date)}`}
                            </Button>
                          ))}
                          {!line.LinkedInvoices?.length ? (
                            <Text as="span" tone="subdued">
                              No invoice linked
                            </Text>
                          ) : null}
                        </InlineStack>
                      </BlockStack>
                    </div>
                  ))}
                </BlockStack>
              </div>
            </div>
          )}
        </Modal.Section>
      </Modal>
      {open ? (
        <style>{`
          .Polaris-Modal-Dialog__Modal {
            width: min(96vw, 1600px) !important;
            max-width: 96vw !important;
          }
        `}</style>
      ) : null}
    </>
  );
}
